import {useState} from 'react';
import {useBiqli} from '@biqli/analytics/react';
import PublicOAuthSmoke from './PublicOAuthSmoke';

type Result = {
  kind: 'idle' | 'success' | 'error';
  message: string;
  data?: unknown;
};

const shortDomain = import.meta.env.VITE_BIQLI_SHORT_DOMAIN?.trim() || 'biq.li';
const shortKey = import.meta.env.VITE_BIQLI_SHORT_KEY?.trim();

function createId(prefix: string) {
  return `${prefix}-${crypto.randomUUID()}`;
}

export default function App() {
  const biqli = useBiqli();
  const [customerExternalId] = useState(() => createId('react-customer'));
  const [result, setResult] = useState<Result>({
    kind: 'idle',
    message: 'Ready. Start by recording a click or arrive with a bq_id query parameter.',
  });
  const [busy, setBusy] = useState(false);
  const [consent, setConsentState] = useState(true);

  async function run(label: string, action: () => Promise<unknown>) {
    setBusy(true);
    try {
      const data = await action();
      setResult({kind: 'success', message: `${label} recorded.`, data});
    } catch (error) {
      setResult({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Unknown tracking error.',
        data: error,
      });
    } finally {
      setBusy(false);
    }
  }

  async function trackOnServer(path: '/api/server/lead' | '/api/server/sale') {
    const clickId = biqli.getClickId();
    if (!clickId) throw new Error('Record or capture a click before sending a server event.');

    const response = await fetch(path, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({clickId, customerExternalId}),
    });
    const payload = await response.json() as {error?: {message?: string}};
    if (!response.ok) throw new Error(payload.error?.message || `Server tracking failed with status ${response.status}.`);
    return payload;
  }

  function setConsent(granted: boolean) {
    biqli.setConsent(granted);
    setConsentState(granted);
    setResult({
      kind: 'idle',
      message: granted ? 'Tracking consent granted.' : 'Tracking consent denied and attribution cleared.',
    });
  }

  return (
    <main>
      <section className="card">
        <p className="eyebrow">External React consumer</p>
        <h1>Biqli tracking smoke test</h1>
        <p className="description">
          The browser uses <code>@biqli/analytics</code>. The private backend uses <code>@biqli/sdk</code>.
        </p>

        <dl>
          <div>
            <dt>Current click ID</dt>
            <dd>{biqli.getClickId() || 'No attribution captured'}</dd>
          </div>
          <div>
            <dt>Test customer</dt>
            <dd>{customerExternalId}</dd>
          </div>
        </dl>

        <div className="actions">
          <button
            disabled={busy || !shortKey || !consent}
            onClick={() => run('Click', () => biqli.trackClick({
              domain: shortDomain,
              key: shortKey,
              url: window.location.href,
              eventId: createId('react-click'),
            }))}
          >
            Browser: record click
          </button>

          <button
            disabled={busy || !consent}
            onClick={() => run('Lead', () => biqli.trackLead({
              eventId: createId('react-lead'),
              eventName: 'React docs smoke lead',
              customerExternalId,
              customerName: 'React Docs Customer',
              customerEmail: `${customerExternalId}@example.com`,
            }))}
          >
            Browser: record lead
          </button>

          <button
            disabled={busy || !consent}
            onClick={() => run('Sale', () => biqli.trackSale({
              eventId: createId('react-sale'),
              eventName: 'React docs smoke purchase',
              customerExternalId,
              customerName: 'React Docs Customer',
              amount: 1299,
              currency: 'usd',
              invoiceId: createId('react-invoice'),
            }))}
          >
            Browser: record $12.99 sale
          </button>

          <button
            disabled={busy || !consent}
            onClick={() => run('Server lead', () => trackOnServer('/api/server/lead'))}
          >
            Server: record lead
          </button>

          <button
            disabled={busy || !consent}
            onClick={() => run('Server sale', () => trackOnServer('/api/server/sale'))}
          >
            Server: record $12.99 sale
          </button>
        </div>

        <label className="consent">
          <input
            type="checkbox"
            checked={consent}
            onChange={event => setConsent(event.target.checked)}
          />
          Tracking consent granted
        </label>

        {!shortKey && (
          <p className="warning">Set VITE_BIQLI_SHORT_KEY before testing manual click tracking.</p>
        )}

        <output className={result.kind}>
          <strong>{result.message}</strong>
          {result.data !== undefined && <pre>{JSON.stringify(result.data, null, 2)}</pre>}
        </output>
      </section>
      <PublicOAuthSmoke />
    </main>
  );
}
